import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Account,
  CreateTppWebFormV2ParamsMultibankingApiProviderEnum,
} from "@rentablo/api/apiv1";
import { useMyself } from "@api";
import { useFeatureFlag } from "@hooks";
import { getUpdateBankConnectionUrl } from "@helpers";
import { createAndRedirectToTppWebForm } from "@rentablo/bank-import";
import { ResponsiveDropdownItem } from "@components";
import UpdateIcon from "@icons/myicons/refresh.svg?react";
import UploadIcon from "@icons/myicons/cloud-storage-upload.svg?react";
import AddIcon from "@icons/myicons/plus.svg?react";
import EditIcon from "@icons/myicons/edit.svg?react";
import UpdateHistoryIcon from "@icons/myicons/time-clock-menu.svg?react";
import ManageBankConnectionIcon from "@icons/myicons/bank-edit.svg?react";
import HideIcon from "@icons/myicons/eye-cross.svg?react";
import UnhideIcon from "@icons/myicons/eye.svg?react";
import CancelIcon from "@icons/myicons/delete-circle.svg?react";
import TransferIcon from "@icons/myicons/wallet.svg?react";

interface Options {
  account?: Account;
  handleHideAccount: () => void;
  handleOpenDeleteModal: () => void;
}

export const useDepotMenuItems = ({
  account,
  handleHideAccount,
  handleOpenDeleteModal,
}: Options): ResponsiveDropdownItem[] => {
  const { t } = useTranslation();
  const { data: myself } = useMyself();
  const isQA = myself?.roles?.includes("ROLE_QUALITY_ASSURANCE");
  const isPdfImportEnabled = useFeatureFlag("PDF_IMPORT");

  const handleReconnect = useCallback(async () => {
    if (!account || !account.bankConnectionStatus?.restoreId) return;

    await createAndRedirectToTppWebForm({
      multibankingApiProvider:
        account.bankConnectionType as CreateTppWebFormV2ParamsMultibankingApiProviderEnum,
      restoreId: account.bankConnectionStatus?.restoreId,
    });
  }, [account]);

  return useMemo<ResponsiveDropdownItem[]>(() => {
    const updateConnectionUrl = getUpdateBankConnectionUrl(account);
    const hasBankConnection = Boolean(account?.bankConnectionType);
    const items: ResponsiveDropdownItem[] = [];

    if (updateConnectionUrl) {
      items.push({
        label: t("depotPage.headerMenu.update"),
        testId: "depot-update",
        to: updateConnectionUrl,
        icon: <UpdateIcon />,
      });
    }

    if (isQA && account?.bankConnectionStatus?.status === "INACTIVE") {
      items.push({
        label: t("depotPage.headerMenu.reconnect"),
        testId: "depot-reconnect",
        onClick: handleReconnect,
        icon: <UpdateIcon />,
      });
    }

    if (isPdfImportEnabled) {
      items.push({
        label: t("depotPage.headerMenu.upload"),
        testId: "pdf-upload",
        to: `/depots/${account?.id}/file-drop`,
        icon: <UploadIcon />,
      });
    }

    items.push(
      {
        label: t("depotPage.headerMenu.uploadCsv"),
        testId: "csv-upload",
        to: `/depots/${account?.id}/csv-drop`,
        icon: <UploadIcon />,
      },
      {
        label: t("depotPage.headerMenu.addButton"),
        testId: "add-investment",
        to: `/new-transaction-wizard?depotId=${account?.id}`,
        icon: <AddIcon width="24px" height="14px" />,
      },
      {
        label: t("depotPage.headerMenu.edit"),
        testId: "edit",
        to: `/depots/${account?.id}/edit`,
        icon: <EditIcon />,
      },
      {
        label: t("depotPage.headerMenu.showUpdateHistory"),
        testId: "synchronization-log",
        to: `/synchronization-logs/${account?.id}`,
        icon: <UpdateHistoryIcon />,
      }
    );

    if (hasBankConnection) {
      items.push({
        label: t("depotPage.headerMenu.manageBankConnection"),
        testId: "manage-bank-connection",
        to: `/settings/bankConnections/${account?.bankConnectionType}/${account?.bankConnectionId}`,
        icon: <ManageBankConnectionIcon />,
      });
    }

    items.push({
      label: t("depotPage.headerMenu.transfer"),
      testId: "depot-transfer",
      to: `/depots/${account?.id}/transfer?redirectUrl=${
        import.meta.env.VITE_APP_V2_URL
      }/depots/${account?.id}`,
      icon: <TransferIcon />,
      startsGroup: true,
    });

    if (hasBankConnection) {
      items.push({
        label: t("depotPage.headerMenu.editReferenceAccount"),
        testId: "edit-reference-account",
        to: `/depots/${account?.id}/reference-account`,
        icon: <EditIcon />,
      });
    }

    items.push({
      label: account?.isHidden
        ? t("depotPage.headerMenu.show")
        : t("depotPage.headerMenu.hide"),
      testId: account?.isHidden ? "unhide-depot" : "hide-depot",
      onClick: handleHideAccount,
      icon: account?.isHidden ? <UnhideIcon /> : <HideIcon />,
    });

    items.push({
      label: t("depotPage.headerMenu.delete"),
      testId: "delete",
      onClick: handleOpenDeleteModal,
      icon: <CancelIcon />,
      color: "error.main",
      startsGroup: true,
    });

    return items;
  }, [
    account,
    t,
    isQA,
    isPdfImportEnabled,
    handleReconnect,
    handleHideAccount,
    handleOpenDeleteModal,
  ]);
};
